import { FC, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Checkbox, Radio, RadioChangeEvent } from 'antd'
import style from './style.m.less'
import { ILogoModal } from './types'

export const LogoModal: FC<ILogoModal> = ({
    visible,
    names,
    availability,
    onClose,
    onSubmit,
    isNewFormat,
    clientLabel = 'Приобретатель АГ',
}) => {
    const [client, setClient] = useState(false)
    const [pending, setPending] = useState(false)

    const [currentRadioId, setCurrentRadioId] = useState<number>(4)

    useEffect(() => {
        if (visible) {
            setClient(false)
            setPending(false)
            setCurrentRadioId(4)
        }
    }, [visible])

    useEffect(() => {
        setClient(false)
        setPending(false)
    }, [currentRadioId])

    const n = useMemo(
        () => ({
            client_name: names?.client_name ?? 'Приобретатель',
            client_type: names?.client_type ?? -1,
            client_id: names?.client_id ?? 'Поставщик',
            user_name: names?.user_name ?? '',
            user_type: names?.user_type ?? -1,
            user_id: names?.user_id ?? '',
        }),
        [names],
    )
    const clientDisabled = availability ? !availability.client_available : false
    const userDisabled = availability ? !availability.user_available : false

    const clientType = n.client_type
    const userType = n.user_type
    const submitDisabled = (clientDisabled && userDisabled) || (!client && currentRadioId === 4)
    const modalWidth: number = isNewFormat ? 600 : 500

    const handleSubmit = async () => {
        try {
            setPending(true)
            let main_logo_id: string
            switch (currentRadioId) {
                case 0:
                    main_logo_id = n.client_id
                    break
                case 1:
                    main_logo_id = n.user_id
                    break
                default:
                    main_logo_id = ''
                    break
            }
            await onSubmit({ client_logo: client, main_logo_id: main_logo_id })
        } finally {
            setPending(false)
        }
    }

    const handleRadioChange = (e: RadioChangeEvent) => {
        setCurrentRadioId(e.target.value)
    }
    return (
        <Modal
            title="Логотип"
            visible={visible}
            onCancel={onClose}
            footer={
                <div
                    className={style?.buttonsWrap}
                    style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}
                >
                    <Button onClick={onClose} className={style?.primaryButtonCancel}>
                        Закрыть
                    </Button>
                    <Button
                        type="primary"
                        className={style?.primaryButton}
                        onClick={handleSubmit}
                        loading={pending}
                        disabled={submitDisabled}
                    >
                        Сформировать
                    </Button>
                </div>
            }
            width={modalWidth}
            style={{ height: 'max-content', borderRadius: 24, overflow: 'hidden', padding: 0 }}
            bodyStyle={{
                width: modalWidth,
                height: 'max-content',
                maxHeight: '800px',
                overflowY: 'auto',
                padding: 20,
            }}
            destroyOnClose
            centered
            closable={false}
            maskClosable={false}
            keyboard={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {isNewFormat && (
                    <div>
                        <p style={{ marginBottom: '8px' }}>
                            Шаг 1. Выберите логотип организации для отображения в заголовке
                            свидетельства (для выбора доступен только один логотип)
                        </p>
                        <Radio.Group
                            onChange={handleRadioChange}
                            value={currentRadioId}
                            style={{ width: '100%' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div className={style.radioRight}>
                                    <span>без логотипа</span>
                                    <Radio value={4} />
                                </div>

                                <div className={style.radioRight}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{n?.client_name}</span>
                                        {!clientDisabled ? (
                                            <span className={style.infoMessage}>
                                                Приобретатель АГ
                                            </span>
                                        ) : (
                                            <span className={style.warningMessage}>
                                                {clientType === 0
                                                    ? 'Логотип не применяется'
                                                    : 'Логотип отсутствует'}
                                            </span>
                                        )}
                                    </div>
                                    <Radio value={0} disabled={clientDisabled} />
                                </div>
                                <div className={style.radioRight}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{n?.user_name}</span>
                                        {!userDisabled ? (
                                            <span className={style.infoMessage}>Поставщик АГ</span>
                                        ) : (
                                            <span className={style.warningMessage}>
                                                {userType === 0
                                                    ? 'Логотип не применяется'
                                                    : 'Логотип отсутствует'}
                                            </span>
                                        )}
                                    </div>
                                    <Radio value={1} disabled={userDisabled} />
                                </div>
                            </div>
                        </Radio.Group>
                        <p style={{ marginTop: '12px', marginBottom: '8px' }}>
                            Шаг 2. Выберите логотипы организаций для отображения внутри
                            свидетельства (доступен множественный выбор)
                        </p>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginRight: 8,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: modalWidth - 130,
                        }}
                    >
                        <span>{n.client_name}</span>
                        {!clientDisabled ? (
                            <span className={style.infoMessage}>{clientLabel}</span>
                        ) : (
                            <span className={style.warningMessage}>
                                {clientType === 0
                                    ? 'Логотип не применяется'
                                    : 'Логотип отсутствует'}
                            </span>
                        )}
                    </div>
                    <Checkbox
                        checked={client}
                        disabled={clientDisabled || currentRadioId === 0}
                        onChange={(e) => setClient(e.target.checked)}
                    />
                </div>
            </div>
        </Modal>
    )
}
